<script setup lang="ts">
import InfiniteLoading from 'v3-infinite-loading'
import { ref, watch } from 'vue'
import { Container, Draggable } from 'vue3-smooth-dnd'

import { useGetCampaigns } from '@/api/campaigns.api'
import LoadingIcon from '@/assets/icons/loading.svg?component'
import CampaignFormPriorityItem from '@/components/CampaignForm/CampaignFormPriority/CampaignFormPriorityItem.vue'
import { type AdvertiserListItemType } from '@/lib/types/advertiser.types'
import { type CampaignFormType } from '@/lib/types/campaign.types'
import { type CampaignsFilterType, CampaignStatusEnum } from '@/lib/types/campaigns.types'
import { type ProcessableImageType } from '@/lib/types/common.types'
import { BoxComponent, FlexComponent } from '@/ui-library'

type PriorityItem = {
  id: number
  advertiserId: number
  advertiserName: string
  advertiserLogo?: ProcessableImageType
  campaignName: string
  currency?: string
  isDraggable?: boolean
  trafficSourceCosts?: CampaignFormType['trafficSourceCosts']
}
type DragCardAction = {
  addedIndex: number
  removedIndex: number
}

const props = defineProps<{
  advertiser: AdvertiserListItemType
  currentCampaignName: string
  trafficSourceCosts: CampaignFormType['trafficSourceCosts']
  currency?: string
  campaignId?: number
  shouldAddNewItem?: boolean
}>()

const items = ref<PriorityItem[]>([])
const getMoreActiveCampaignsLink = ref<string | undefined>()
const filter = ref<CampaignsFilterType>({})
const { data, links, isLoading } = useGetCampaigns(filter, {
  linkRef: getMoreActiveCampaignsLink,
})
const priorityItem = defineModel<number | null>('priorityItem')

const applyDrag = (arr: PriorityItem[], { removedIndex, addedIndex }: DragCardAction) => {
  if (removedIndex === addedIndex) {
    return arr
  }

  const result = [...arr]
  const [itemToAdd] = result.splice(removedIndex, 1)
  result.splice(addedIndex, 0, itemToAdd!)

  return result
}
const onDrop = (dropResult: DragCardAction) => {
  const newIndex = dropResult.addedIndex
  const newArray = applyDrag(items.value, dropResult)

  items.value = newArray
  priorityItem.value = newIndex === 0 ? null : newArray[newIndex - 1]?.id
}

watch(
  [() => props.advertiser, () => props.shouldAddNewItem],
  ([advertiser, shouldAddNewItem]) => {
    getMoreActiveCampaignsLink.value = undefined
    if (shouldAddNewItem) {
      items.value = [
        {
          id: 1,
          advertiserLogo: advertiser.logo,
          advertiserName: advertiser.label,
          advertiserId: advertiser.id,
          campaignName: props.currentCampaignName,
          trafficSourceCosts: props.trafficSourceCosts,
          currency: props.currency,
          isDraggable: true,
        },
      ]
    }

    filter.value = {
      advertiser,
      statuses: [CampaignStatusEnum.Active, CampaignStatusEnum.Scheduled],
      sortBy: 'priority:desc',
      orId: props.campaignId,
    }
  },
  { immediate: true },
)

watch(
  () => props.currentCampaignName,
  (campaignName) => {
    items.value = items.value.map((item) => {
      if (item.isDraggable) {
        return { ...item, campaignName }
      }
      return item
    })
  },
  { immediate: true },
)

watch(
  () => props.currency,
  (currency) => {
    items.value = items.value.map((item) => {
      if (item.isDraggable) {
        return { ...item, currency }
      }
      return item
    })
  },
  { immediate: true },
)

watch(
  () => props.trafficSourceCosts,
  (trafficSourceCosts) => {
    items.value = items.value.map((item) => {
      if (item.isDraggable) {
        return { ...item, trafficSourceCosts }
      }
      return item
    })
  },
  { immediate: true },
)

watch(
  () => data.value,
  (activeCampaignsList) => {
    const existedItems = (activeCampaignsList || []).map((item) => ({
      id: item.id,
      advertiserLogo: item.advertiserLogo,
      advertiserName: item.advertiserName,
      advertiserId: item.advertiserId,
      currency: item.advertiserCurrency,
      isDraggable: item.id === props.campaignId,
      campaignName: item.internalName,
      trafficSourceCosts: item.trafficSourceCosts,
    }))

    items.value = items.value.concat(existedItems)
  },
  { immediate: true },
)
</script>
<template>
  <div>
    <BoxComponent :mb="40" class="campaign-form-priority--wrapper">
      <Container
        drag-handle-selector=".campaign-form-priority-item__container-draggable"
        class="campaign-form-priority--container"
        @drop="onDrop"
      >
        <Draggable v-for="element in items" :key="element.id">
          <CampaignFormPriorityItem
            :is-draggable="element.isDraggable"
            :campaign-name="element.campaignName"
            :advertiser-name="element.advertiserName"
            :advertiser-id="element.id"
            :currency="element.currency"
            :traffic-source-costs="element.trafficSourceCosts"
          />
        </Draggable>
        <InfiniteLoading @infinite="() => (getMoreActiveCampaignsLink = links.next)">
          <template #spinner>
            <FlexComponent v-if="isLoading" justify="center">
              <BoxComponent :mt="8" :mb="8">
                <el-icon :size="48">
                  <LoadingIcon />
                </el-icon>
              </BoxComponent>
            </FlexComponent>
          </template>
        </InfiniteLoading>
      </Container>
    </BoxComponent>
  </div>
</template>
<style lang="scss" scoped>
.campaign-form-priority--container {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.campaign-form-priority--wrapper {
  max-height: 600px;
  margin: 0 -24px;
  padding: 0 24px;
  overflow: auto;
}
:deep(.sortable-chosen.campaign-form-priority-item__container-draggable) {
  opacity: 0.5;
  background: #c8ebfb;
}
</style>
